import {Address} from '../models/mfl';
import {GeocodedSuburb} from '../models/middleware';

/**
 * Stable, collision-free identity for a geocoded suburb, for use as a React
 * list key or dropdown row value.
 *
 * Neither coordinates nor locality alone are unique in Australian geodata:
 * distinct suburbs can share a rounded `lat`/`lon`, and a locality name repeats
 * across states (e.g. ST AGNES exists in QLD 4671 and SA 5097). Keying a result
 * list by a non-unique field makes rows collide and visually stack across
 * keystrokes. locality + state + postcode is unique.
 */
export const suburbKey = (suburb: GeocodedSuburb): string =>
  `${suburb.locality}-${suburb.administrative_area}-${suburb.postal_code}`;

export const renderAddressLines = (lines: string[] = []) =>
  lines[0] && lines[1] ? lines.join(', ') : lines[0] || '';

/**
 * Formats a full address for display following Australia Post standards.
 *
 * MFL/Australia Post format:
 * - lines[0] = Unit/Apartment (optional)
 * - lines[1] = Street address
 * - Last line: SUBURB STATE POSTCODE (with spaces between)
 *
 * Example output: "Unit 4, 123 Main Street, Sydney NSW 2000"
 *
 * @see https://auspost.com.au/sending/guidelines/addressing-guidelines
 */
export const formatFullAddress = (address: Address | undefined | null): string => {
  if (!address) return '';

  const parts: string[] = [];

  // Add address lines in order (MFL format: unit first, street second)
  // Strip trailing punctuation to avoid ",." when joining
  if (address.lines?.length) {
    const nonEmptyLines = address.lines.filter(line => line?.trim());
    nonEmptyLines.forEach(line => parts.push(line.trim().replace(/[.,]+$/, '')));
  }

  // Add suburb, state and postcode (with spaces between per Australia Post)
  const locationParts: string[] = [];
  if (address.locality) locationParts.push(address.locality);
  if (address.administrative_area) locationParts.push(address.administrative_area);
  if (address.postal_code) locationParts.push(address.postal_code);

  if (locationParts.length) {
    parts.push(locationParts.join(' '));
  }

  return parts.join(', ');
};
